import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import DeployWizard from './components/DeployWizard';
import AgentWorkspace from './components/AgentWorkspace';
import Documents from './components/Documents';
import Reports from './components/Reports';
import AdminPanel from './components/AdminPanel';
import Integrations from './components/Integrations';
import OwnerSubscribers from './components/OwnerSubscribers';
import OwnerUsers from './components/OwnerUsers';
import OwnerFinancials from './components/OwnerFinancials';
import SupportChat from './components/SupportChat';
import SettingsPage from './components/SettingsPage';
import MyAITeam from './components/MyAITeam';
import FeatureRequests from './components/FeatureRequests';
import LandingPage from './components/LandingPage';
import AgentProfilePage from './components/AgentProfilePage';
import EmployeeDirectory from './components/EmployeeDirectory';
import Announcements from './components/Announcements';
import TemplateLibrary from './components/TemplateLibrary';
import Community from './components/Community';
import HelpCenter from './components/HelpCenter';
import SubscriptionPaywall from './components/SubscriptionPaywall';
import GuidedSetup from './components/GuidedSetup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// API instance
export const api = axios.create({ baseURL: BACKEND_URL });

// Auth context
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('hr_catalyst_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/api/auth/me').then(res => {
        setUser(res.data);
        setLoading(false);
      }).catch(() => {
        localStorage.removeItem('hr_catalyst_token');
        setToken(null);
        setUser(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('hr_catalyst_token', res.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await api.post('/api/auth/register', { name, email, password });
    localStorage.setItem('hr_catalyst_token', res.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('hr_catalyst_token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
    } catch (e) { console.error('User refresh error:', e); }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, refreshUser } = useAuth();
  const [showPaywall, setShowPaywall] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [checked, setChecked] = useState(false);

  const checkSubscriptionAndSetup = useCallback(async () => {
    if (!user || user.is_internal) { setChecked(true); return; }
    
    // Check if returning from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      const plan = urlParams.get('plan') || 'starter';
      try { await api.post('/api/subscription/start-trial', { plan, card_provided: true }); } catch(e) { console.error(e); }
      window.history.replaceState({}, '', window.location.pathname);
      setChecked(true); return;
    }
    
    try { await refreshUser(); } catch(e) { console.error(e); }
    try {
      const subRes = await api.get('/api/subscription/status');
      if (subRes.data?.needs_subscription) { setShowPaywall(true); setChecked(true); return; }
      const onbRes = await api.get('/api/onboarding/status');
      if (!onbRes.data?.completed) { setShowSetup(true); }
    } catch(e) { console.error(e); }
    setChecked(true);
  }, [user, refreshUser]);

  useEffect(() => {
    if (user && !checked) checkSubscriptionAndSetup();
  }, [user, checked, checkSubscriptionAndSetup]);

  // Re-check after company setup completes (triggered by Layout)
  useEffect(() => {
    const handler = () => { setChecked(false); };
    window.addEventListener('company-setup-complete', handler);
    return () => window.removeEventListener('company-setup-complete', handler);
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /><p>Loading Panzix HR AI Workforce...</p></div>;
  if (!user) return <Navigate to="/login" />;
  
  return (
    <>
      {showPaywall && !user.is_internal && <SubscriptionPaywall onComplete={() => { setShowPaywall(false); setShowSetup(true); }} />}
      {showSetup && !showPaywall && !user.is_internal && <GuidedSetup onComplete={() => setShowSetup(false)} onSkip={() => setShowSetup(false)} />}
      {children}
    </>
  );
}

function OwnerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /><p>Loading Panzix HR AI Workforce...</p></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'owner' && user.role !== 'panzix_employee') return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/agent/:agentType" element={<AgentProfilePage />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/community-public" element={<Community />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="deploy" element={<DeployWizard />} />
        <Route path="workspace/:agentId" element={<AgentWorkspace />} />
        <Route path="documents" element={<Documents />} />
        <Route path="reports" element={<Reports />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="my-team" element={<MyAITeam />} />
        <Route path="admin" element={<AdminPanel />} />
        <Route path="support" element={<SupportChat />} />
        <Route path="owner/subscribers" element={<OwnerRoute><OwnerSubscribers /></OwnerRoute>} />
        <Route path="owner/users" element={<OwnerRoute><OwnerUsers /></OwnerRoute>} />
        <Route path="owner/financials" element={<OwnerRoute><OwnerFinancials /></OwnerRoute>} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="feature-requests" element={<FeatureRequests />} />
        <Route path="employees" element={<EmployeeDirectory />} />
        <Route path="announcements" element={<Announcements />} />
        <Route path="templates" element={<TemplateLibrary />} />
        <Route path="community" element={<Community />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}
